import { Component, computed, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { RouterLink } from '@angular/router'
import { generateInitials } from '@/app/utils/string'
import { Icon } from '@app/components/icon/icon'
import { AuthService, authenticatedImageSrc } from '@core/services/api'
import { UserInfoState } from '@core/store/user/user.state'
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngxs/store'

type UserProfileMenuType = {
  icon: string
  label: string
  href: string
  className?: string
  isDivider?: boolean
  action?: string
}

@Component({
  selector: 'simple-user-dropdown',
  imports: [Icon, RouterLink, NgbDropdownModule],
  template: `
    <div id="simple-user-dropdown" class="topbar-item nav-user">
      <div ngbDropdown placement="bottom-end">
        <button
          type="button"
          class="topbar-link dropdown-toggle drop-arrow-none px-2 border-0 bg-transparent"
          ngbDropdownToggle
        >
          @if (profileImageSrc()) {
            <img
              [src]="profileImageSrc()"
              width="32"
              height="32"
              class="rounded-circle me-lg-2 d-flex object-fit-cover"
              alt="user-image"
            />
          } @else {
            <span class="avatar-sm me-lg-2 d-flex">
              <span class="avatar-title text-bg-primary fw-bold rounded-circle">
                {{ userInitials() }}
              </span>
            </span>
          }
          <div class="d-lg-flex align-items-center gap-1 d-none">
            <div class="text-start lh-sm">
              <h5 class="my-0 nav-user-name">{{ username() }}</h5>
              @if (position()) {
                <span class="pro-username d-block">{{ position() }}</span>
              }
            </div>
            <app-icon icon="chevron-down" class="align-middle"></app-icon>
          </div>
        </button>
        <div ngbDropdownMenu class="dropdown-menu-end">
          @for (item of userProfileMenuData; track item.label) {
            <a [routerLink]="item.href" ngbDropdownItem [class]="item.className" (click)="handleClick(item)">
              <app-icon [icon]="item.icon" class="me-1 fs-lg align-middle"></app-icon>

              <span class="align-middle">{{ item.label }}</span>
            </a>

            @if (item.isDivider) {
              <div class="dropdown-divider"></div>
            }
          }
        </div>
      </div>
    </div>
  `,
  styles: ``,
})
export class SimpleUserDropdown {
  private auth = inject(AuthService)
  private store = inject(Store)

  private user = toSignal(this.store.select(UserInfoState.getUser), { initialValue: null })

  username = computed(() => {
    const user = this.user()
    if (!user) {
      return ''
    }

    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
    return fullName || user.email || ''
  })

  position = computed(() => {
    const user = this.user()
    return user?.user_role_info?.user_role_name ?? ''
  })

  profileImageUrl = computed(() => {
    const user = this.user()
    return user?.profile_img_file_info?.file_url ?? ''
  })

  profileImageSrc = authenticatedImageSrc(this.profileImageUrl)

  userInitials = computed(() => {
    const user = this.user()
    if (!user) {
      return ''
    }

    const firstInitial = user.first_name?.charAt(0) ?? ''
    const lastInitial = user.last_name?.charAt(0) ?? ''
    const initials = `${firstInitial}${lastInitial}`.trim()

    if (initials) {
      return initials.toUpperCase()
    }

    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
    return generateInitials(fullName || user.email)
  })

  userProfileMenuData: UserProfileMenuType[] = [
    {
      icon: 'circle-user-round',
      label: 'บัญชีผู้ใช้งาน',
      href: '/profile',
      isDivider: true,
    },
    {
      icon: 'log-out',
      label: 'ออกจากระบบ',
      href: '',
      action: 'logout',
      className: 'fw-semibold text-danger',
    },
  ]

  handleClick(item: UserProfileMenuType) {
    if (item.action === 'logout') {
      this.auth.logout().subscribe()
    }
  }
}
